import React, { useState, useEffect } from "react";
import Plot from "react-plotly.js";
import "../styles/PagesModern.css";
import { useSeriesCategories } from "../hooks/useSeriesCategories";
import { getIconFileName } from "../utils/iconUtils";
import CategoryIcon from "./CategoryIcon";
import { LoadingPanel, PageHeader, PanelHeader, SegmentedControl } from "./RefreshUI";
import { CHART, chartAxis, chartBase, chartWatermark, panelChartWidth } from "../utils/chartTheme";
import { CATEGORIES, CATEGORY_LABELS } from "../utils/racingLabels";

const SeriesSafety = () => {
        const [data, setData] = useState([]);
        const [historicData, setHistoricData] = useState([]);
        const [filteredData, setFilteredData] = useState([]);
        const [filteredHistoricData, setFilteredHistoricData] = useState([]);
        const [isError, setError] = useState(false);
        const [page, setPage] = useState(0);
        const [isLoading, setIsLoading] = useState(true);
        const [isMobile, setIsMobile] = useState(window.innerWidth <= 760);
        const [pageSize, setPageSize] = useState(isMobile ? 5 : 12);
        const [dataType, setDataType] = useState("all");
        const [minTotalEntrants, setMinTotalEntrants] = useState(100);
        const [showCurrentWeek, setShowCurrentWeek] = useState(false);
        const [showCurrentSeason, setShowCurrentSeason] = useState(false);
        const [weekNumber, setWeekNumber] = useState(0);
        const seriesCategoryMap = useSeriesCategories();

        useEffect(() => {
                async function fetchWeekNumber() {
                        try {
                                const response = await fetch("https://iracing6-backend.herokuapp.com/api/week-number");
                                const jsonData = await response.json();
                                setWeekNumber(jsonData);
                        } catch (error) {
                                console.error("Error fetching week number:", error);
                        }
                }

                fetchWeekNumber();
        }, []);

        useEffect(() => {
                const fetchData = async () => {
                        try {
                                const response = await fetch("https://iracing6-backend.herokuapp.com/api/series-safety/");
                                const data = await response.json();
                                data.sort((a, b) => b.totalCorners / b.totalIncidents - a.totalCorners / a.totalIncidents);
                                setData(data);
                                setIsLoading(false);
                        } catch (error) {
                                setError(true);
                        }
                };

                fetchData();
        }, []);

        useEffect(() => {
                const fetchHistoricData = async () => {
                        try {
                                const response = await fetch("https://iracing6-backend.herokuapp.com/api/series-safety/historic/");
                                const data = await response.json();
                                data.sort((a, b) => b.totalCorners / b.totalIncidents - a.totalCorners / a.totalIncidents);
                                setHistoricData(data);
                                setIsLoading(false);
                        } catch (error) {
                                setError(true);
                        }
                };

                fetchHistoricData();
        }, []);

        useEffect(() => {
                // Filter using API category data
                const filtered = data.filter((item) => {
                        const categoryMatch = dataType === "all" || seriesCategoryMap[item.season_name] === dataType;
                        const entrantsMatch = item.totalEntrants >= minTotalEntrants;
                        return categoryMatch && entrantsMatch;
                });
                setFilteredData(filtered);
                setPage(0); // Reset page number whenever dataType changes

                const filteredHistoric = historicData.filter((item) => {
                        const categoryMatch = dataType === "all" || seriesCategoryMap[item.season_name] === dataType;
                        const entrantsMatch = item.totalEntrants >= minTotalEntrants;
                        return categoryMatch && entrantsMatch;
                });
                setFilteredHistoricData(filteredHistoric);
        }, [dataType, data, historicData, minTotalEntrants, seriesCategoryMap]);

        const header = <PageHeader title="Series safety" lede="Corners driven per incident point across official races. Higher is safer." />;

        if (isLoading || isError) {
                return (
                        <div className="pg">
                                <main className="hp-main">
                                        {header}
                                        {isError ? (
                                                <section className="hp-panel pg-loading">
                                                        <p className="hp-panel__note">Safety data couldn't load. Refresh the page to try again.</p>
                                                </section>
                                        ) : (
                                                <LoadingPanel />
                                        )}
                                </main>
                        </div>
                );
        }

        // Calculate the total number of pages
        const pageCount = Math.ceil(filteredData.length / pageSize);

        // Calculate the data for the current page
        const currentData = filteredData.slice(page * pageSize, (page + 1) * pageSize);
        const currentHistoricData = filteredHistoricData.slice(page * pageSize, (page + 1) * pageSize);

        const changePage = (newPage) => {
                if (newPage >= 0 && newPage < pageCount) {
                        setPage(newPage);
                }
        };

        // Helper to build consistent series labels used on x-axis
        const formatSeriesLabel = (item) =>
                `${item.season_name
                        .replace("2024 Season 4", "")
                        .replace(/-/g, " ")
                        .replace(/series/gi, "")
                        .replace(/iRacing/gi, "")
                        .replace(/Racing/gi, "")
                        .replace(/Dallara/gi, "")
                        .replace(/Pure Driving School/gi, "")
                        .replace(/Fanatec Cup/gi, "")
                        .replace(/\s+/g, " ")
                        .replace(/Fixed  Fixed/gi, "Fixed")
                        .replace(/Maconi Setup Shop/gi, "Maconi")
                        .replace(/Moza Racing/gi, "")
                        .trim()}<br>(${item.totalEntrants})`;

        const displaySeasonName = (name) => name.replace(/\s*-?\s*\d{4} Season \d+/g, "").trim();
        const figWidth = panelChartWidth();
        const margin = isMobile ? { l: 48, r: 8, t: 16, b: 110 } : { l: 64, r: 16, t: 16, b: 110 };
        const plotAreaWidth = Math.max(1, figWidth - margin.l - margin.r);

        const renderChart = (rows) => (
                <div className="hp-chart">
                        <Plot
                                data={[
                                        {
                                                x: rows.map((item) => formatSeriesLabel(item)),
                                                y: rows.map((item) => (item.totalCorners / item.totalIncidents).toFixed(2)),
                                                type: "bar",
                                                marker: { color: CHART.blue },
                                                hovertemplate: "%{y:.2f} corners/incident<br>%{x}<extra></extra>",
                                        },
                                ]}
                                layout={{
                                        ...chartBase(),
                                        margin,
                                        width: figWidth,
                                        height: 520,
                                        bargap: 0.3,
                                        yaxis: { ...chartAxis(), title: { text: "Corners per incident", font: { size: 12, color: CHART.muted } }, automargin: true },
                                        xaxis: { ...chartAxis(), showgrid: false, showticklabels: false, automargin: true, type: "category" },
                                        images: rows.map((item, idx) => {
                                                const n = Math.max(rows.length, 1);
                                                return {
                                                        source: `${process.env.PUBLIC_URL}/assets/series-icons/${getIconFileName(item.season_name)}.png`,
                                                        xref: "paper",
                                                        yref: "paper",
                                                        x: (idx + 0.5) / n, // center of each category across paper [0,1]
                                                        y: -0.02,
                                                        xanchor: "center",
                                                        yanchor: "top",
                                                        sizex: Math.min(0.75 / n, 52 / plotAreaWidth),
                                                        sizey: 0.07,
                                                        sizing: "contain",
                                                        layer: "above",
                                                };
                                        }),
                                        annotations: [
                                                ...rows.map((item, idx) => ({
                                                        text: `${item.totalEntrants.toLocaleString()}<br>entries`,
                                                        xref: "paper",
                                                        yref: "paper",
                                                        x: (idx + 0.5) / Math.max(rows.length, 1),
                                                        y: -0.14,
                                                        showarrow: false,
                                                        xanchor: "center",
                                                        yanchor: "top",
                                                        font: { size: isMobile ? 9 : 10, color: CHART.muted },
                                                })),
                                                // Sorted safest first, so the top-right is empty.
                                                chartWatermark("top-right"),
                                        ],
                                        showlegend: false,
                                }}
                                config={{ displayModeBar: false }}
                        />
                </div>
        );

        const renderTable = (rows) => (
                <div className="pg-table-wrap">
                        <table className="pg-table">
                                <thead>
                                        <tr>
                                                <th>Series</th>
                                                <th className="pg-num">Corners raced</th>
                                                <th className="pg-num">Incidents</th>
                                                <th className="pg-num">Corners per incident</th>
                                        </tr>
                                </thead>
                                <tbody>
                                        {rows.map((item) => (
                                                <tr key={item.season_name}>
                                                        <td>
                                                                <span className="pg-series">
                                                                        <img src={`${process.env.PUBLIC_URL}/assets/series-icons/${getIconFileName(item.season_name)}.png`} alt="" />
                                                                        <span className="pg-series__name">{displaySeasonName(item.season_name)}</span>
                                                                </span>
                                                        </td>
                                                        <td className="pg-num">{item.totalCorners.toLocaleString()}</td>
                                                        <td className="pg-num">{item.totalIncidents.toLocaleString()}</td>
                                                        <td className="pg-num">{(item.totalCorners / item.totalIncidents).toFixed(2)}</td>
                                                </tr>
                                        ))}
                                </tbody>
                        </table>
                </div>
        );

        const pager = (
                <div className="pg-pager">
                        <button type="button" className="pg-pager__btn" disabled={page === 0} onClick={() => changePage(page - 1)}>
                                Previous
                        </button>
                        <span>{pageCount ? `Page ${page + 1} of ${pageCount}` : "No series match these filters"}</span>
                        <button type="button" className="pg-pager__btn" disabled={(page + 1) * pageSize >= filteredData.length} onClick={() => changePage(page + 1)}>
                                Next
                        </button>
                </div>
        );

        return (
                <div className="pg">
                        <main className="hp-main">
                                {header}

                                <section className="hp-panel pg-filters" aria-label="Filters">
                                        <div className="pg-field">
                                                <span className="pg-field__label">Category</span>
                                                <SegmentedControl
                                                        label="Category"
                                                        value={dataType}
                                                        onChange={setDataType}
                                                        options={[
                                                                { value: "all", title: "All categories", content: "All" },
                                                                ...CATEGORIES.map((cat) => ({ value: cat, title: CATEGORY_LABELS[cat], content: <CategoryIcon category={cat} size={16} /> })),
                                                        ]}
                                                />
                                        </div>
                                        <div className="pg-field">
                                                <label className="pg-field__label" htmlFor="ss-min-entrants">
                                                        Minimum entrants
                                                </label>
                                                <input id="ss-min-entrants" className="pg-input" type="number" value={minTotalEntrants} onChange={(e) => setMinTotalEntrants(e.target.value)} />
                                        </div>
                                </section>

                                <section className="hp-panel">
                                        <PanelHeader
                                                title={`This week (week ${weekNumber} of 13)`}
                                                action={
                                                        <button type="button" className="hp-link" onClick={() => setShowCurrentWeek(!showCurrentWeek)}>
                                                                {showCurrentWeek ? "Hide table" : "Show table"}
                                                        </button>
                                                }
                                        />
                                        {renderChart(currentData)}
                                        {pager}
                                        {showCurrentWeek && renderTable(filteredData)}
                                </section>

                                <section className="hp-panel">
                                        <PanelHeader
                                                title="Whole season"
                                                action={
                                                        <button type="button" className="hp-link" onClick={() => setShowCurrentSeason(!showCurrentSeason)}>
                                                                {showCurrentSeason ? "Hide table" : "Show table"}
                                                        </button>
                                                }
                                        />
                                        {renderChart(currentHistoricData)}
                                        {pager}
                                        {showCurrentSeason && renderTable(filteredHistoricData)}
                                </section>

                                <p className="pg-fine">
                                        Includes all official race sessions: the corners driven by every driver against the incident points they collected, shown as corners per incident point.
                                </p>
                        </main>
                </div>
        );
};

export default SeriesSafety;
